/**
 * Application entrypoint: loads runtime config, renders the appropriate app
 * state (configuration error / disabled / active referral form), and wires up
 * validation, submission, and TurboVets postMessage autofill.
 */
import "./styles.css";
import { ConfigError, loadConfig, type RuntimeConfig } from "./config";
import { createReferralClient, ReferralApiError } from "./api";
import { installAutofillListener } from "./autofill";
import { buildPayload } from "./form/payload-builder";
import { readFormState, renderReferralForm } from "./form/referral-form";
import { validatePurchase, validateRefinance } from "./form/validation";

const BRAND_HEADER = `
  <header class="brand">
    <div class="brand-mark">loan<span>Depot</span></div>
    <div class="brand-sub">TurboVets Referral</div>
  </header>`;

export function renderConfigError(root: HTMLElement, message: string): void {
  root.innerHTML = `
    ${BRAND_HEADER}
    <main class="state state-error" role="alert">
      <h1>Configuration error</h1>
      <p>The referral form could not start because its configuration is invalid.</p>
      <p class="detail">${escapeHtml(message)}</p>
    </main>`;
}

export function renderDisabled(root: HTMLElement): void {
  root.innerHTML = `
    ${BRAND_HEADER}
    <main class="state state-disabled">
      <h1>Referral form unavailable</h1>
      <p>This referral form is temporarily unavailable. Please check back later.</p>
    </main>`;
}

export function renderAppShell(root: HTMLElement, config: RuntimeConfig): HTMLFormElement {
  root.innerHTML = `
    ${BRAND_HEADER}
    <main class="app">
      <p class="intro">Complete the referral below. Your details are reviewed before anything is sent to loanDepot.</p>
      <div id="prefill-notice" class="notice" hidden role="status"></div>
      <div id="form-mount"></div>
      <div id="validation-summary" class="validation-summary" hidden role="alert"></div>
      <div id="result" class="result" hidden aria-live="polite"></div>
      <p class="env-tag">Environment: ${escapeHtml(config.environment)}</p>
    </main>`;

  const mount = root.querySelector<HTMLElement>("#form-mount");
  if (!mount) throw new Error("Missing form mount point.");
  return renderReferralForm(mount);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function showValidationSummary(root: ParentNode, errors: string[]): void {
  const summary = root.querySelector<HTMLElement>("#validation-summary");
  if (!summary) return;
  if (errors.length === 0) {
    summary.hidden = true;
    summary.innerHTML = "";
    return;
  }
  summary.hidden = false;
  summary.innerHTML = `
    <h2>Please fix the following:</h2>
    <ul>${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")}</ul>`;
}

function showResult(root: ParentNode, html: string, kind: "success" | "error"): void {
  const result = root.querySelector<HTMLElement>("#result");
  if (!result) return;
  result.hidden = false;
  result.className = `result result-${kind}`;
  result.innerHTML = html;
}

function showPrefillNotice(root: ParentNode, count: number): void {
  const notice = root.querySelector<HTMLElement>("#prefill-notice");
  if (!notice) return;
  notice.hidden = false;
  notice.textContent = `We pre-filled ${count} field${count === 1 ? "" : "s"} from TurboVets. Please review everything before submitting.`;
}

export function wireSubmit(
  root: HTMLElement,
  form: HTMLFormElement,
  config: RuntimeConfig,
  fetchFn: typeof fetch = fetch,
): void {
  const client = createReferralClient(config, { fetchFn });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void handleSubmit(root, form, client);
  });
}

async function handleSubmit(
  root: HTMLElement,
  form: HTMLFormElement,
  client: ReturnType<typeof createReferralClient>,
): Promise<void> {
  const resultEl = root.querySelector<HTMLElement>("#result");
  if (resultEl) {
    resultEl.hidden = true;
    resultEl.className = "result";
    resultEl.innerHTML = "";
  }

  const state = readFormState(form);
  const built = buildPayload(state);
  if (!built.ok) {
    showValidationSummary(root, [built.error]);
    return;
  }

  const errors =
    built.purpose === "purchase"
      ? validatePurchase(built.request)
      : validateRefinance(built.request);

  if (errors.length > 0) {
    showValidationSummary(root, errors);
    return;
  }
  showValidationSummary(root, []);

  const submitButton = form.querySelector<HTMLButtonElement>("#submit-referral");
  if (submitButton) submitButton.disabled = true;

  try {
    const response =
      built.purpose === "purchase"
        ? await client.createPurchaseReferral(built.request)
        : await client.createRefinanceReferral(built.request);

    const links: string[] = [];
    if (response.links?.status) links.push(`<a href="${escapeHtml(response.links.status)}">Status</a>`);
    if (response.links?.detail) links.push(`<a href="${escapeHtml(response.links.detail)}">Detail</a>`);

    showResult(
      root,
      `
        <h2>Referral submitted</h2>
        <dl>
          <dt>Referral ID</dt><dd>${escapeHtml(response.referralId)}</dd>
          ${response.partnerReferenceId ? `<dt>Partner reference</dt><dd>${escapeHtml(response.partnerReferenceId)}</dd>` : ""}
          <dt>Status</dt><dd>${escapeHtml(response.status)}</dd>
          <dt>Submitted at</dt><dd>${escapeHtml(response.submittedAt)}</dd>
        </dl>
        ${links.length ? `<p class="links">${links.join(" · ")}</p>` : ""}`,
      "success",
    );
  } catch (err) {
    const message =
      err instanceof ReferralApiError
        ? err.message
        : "Something went wrong while submitting the referral. Please try again.";
    showResult(root, `<h2>Referral not submitted</h2><p>${escapeHtml(message)}</p>`, "error");
  } finally {
    if (submitButton) submitButton.disabled = false;
  }
}

export async function bootstrap(root: HTMLElement, fetchFn: typeof fetch = fetch): Promise<void> {
  let config: RuntimeConfig;
  try {
    config = await loadConfig(fetchFn);
  } catch (err) {
    const message = err instanceof ConfigError ? err.message : "Unexpected configuration failure.";
    renderConfigError(root, message);
    return;
  }

  if (!config.enabled) {
    renderDisabled(root);
    return;
  }

  const form = renderAppShell(root, config);
  wireSubmit(root, form, config, fetchFn);

  installAutofillListener({
    allowedOrigins: config.allowedPrefillOrigins,
    root: form,
    onApplied: (fields) => {
      if (fields.length > 0) showPrefillNotice(root, fields.length);
    },
  });
}

if (typeof document !== "undefined") {
  const appRoot = document.getElementById("app");
  if (appRoot) {
    void bootstrap(appRoot);
  }
}
